import LogoutIcon from '@mui/icons-material/Logout'
import PersonOutlineTwoToneIcon from '@mui/icons-material/PersonOutlineTwoTone'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import {StrictMode, useState} from 'react'
import type {BaseSyntheticEvent, FC, ReactElement} from 'react'
import {NavLink} from 'react-router-dom'
import {getUserFullName} from '@app/util-functions'
import {
  useGetOrganizationsQuery,
  useUpdateTenantsCurrentUserMutation,
} from '@apis/infrastructureTenants'
import {useAuthContext} from '@containers/App/AuthProvider'
import type {AuthContextProps} from '@containers/App/AuthProvider'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import type {UserInterface} from '@interfaces/user'
import type {TenantDetailInterface} from '@interfaces/tenants'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {usePagedOptions} from '@shared/hooks/usePagedOptions'
import {useSeenOptionNames, withUnlistedSelection} from '@shared/hooks/useSeenOptionNames'
import Picker from '@shared/components/base/inputs/Picker'

type Props = {
  isMobile?: boolean
}

const AppbarProfile: FC<Props> = ({isMobile}): ReactElement => {
  const {logoutUser} = useAuthContext() as AuthContextProps
  const {currentUser} = useUserContext() as UserContextProps
  const [t] = useCustomTranslation('common')
  const [t2] = useCustomTranslation('settings')
  const [anchorEl, setAnchorEl] = useState(null)
  const handleClick = (event: BaseSyntheticEvent): void => {
    setAnchorEl(event.currentTarget)
  }
  const fullName = getUserFullName(currentUser as UserInterface)
  const currentTenantId = currentUser?.userprofile?.current_tenant
  const currentTenant = currentUser?.tenants?.find((tenant) => tenant.id === currentTenantId)
  const [changeCurrentTenant] = useUpdateTenantsCurrentUserMutation()

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  // The org picker is backed by the server-side organizations list: it already
  // returns only the user's own orgs for a regular user and ALL orgs for a
  // platform (staff/superuser) user. With no search term the `current_organization`
  // pin puts the current org first; the list then grows on demand instead of
  // ending at the ten rows it used to ask for.
  const useOrganizationPage = ({
    search,
    limit,
  }: {
    search: string
    limit: number
  }): {data?: {count: number; results: TenantDetailInterface[]}; isFetching: boolean} =>
    useGetOrganizationsQuery(
      {
        limit,
        offset: 0,
        ordering: 'name',
        ...(search ? {search} : currentTenantId ? {currentOrganization: currentTenantId} : {}),
      },
      {skip: !open || !currentUser},
    )

  const organizations = usePagedOptions(useOrganizationPage)

  // So the selected org still has a name while a search is active (the matches
  // need not include it) or before the first fetch resolves. For a platform user
  // on a non-member org the name comes from the pinned query result once it
  // loads.
  const organizationNames = useSeenOptionNames([
    ...(currentTenant ? [{id: currentTenant.id, name: currentTenant.name}] : []),
    ...organizations.rows.map((organization) => ({
      id: organization.id,
      name: organization.name,
    })),
  ])

  const handleTenantChange = async (tenantId: string) => {
    try {
      await changeCurrentTenant({body: {current_organization_id: tenantId}}).unwrap()
      window.location.href = '/'
    } catch (err) {
      console.error('Failed to change tenant:', err)
    }
  }

  return (
    <StrictMode>
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Tooltip title={t('profile.title')} arrow>
          <IconButton
            id="mbai-appbar-profile-button"
            data-testid="mbai-appbar-profile-button"
            data-cy="mbai-appbar-profile-button"
            aria-describedby={id}
            onClick={handleClick}
            sx={{p: 1}}
          >
            <PersonOutlineTwoToneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {currentTenant && (
          <Typography
            data-testid="mbai-appbar-profile-tenant-name"
            noWrap
            sx={{
              mt: '-6px',
              maxWidth: 96,
              fontSize: '0.55rem',
              lineHeight: 1,
              color: 'text.secondary',
              opacity: 0.7,
              pointerEvents: 'none',
            }}
          >
            {currentTenant.name}
          </Typography>
        )}
      </Box>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{mt: 2.5}}
      >
        <Paper sx={{width: 320, maxWidth: '100%'}}>
          <MenuList>
            <MenuItem>
              <ListItemText
                sx={{color: 'text.secondary'}}
                primary={fullName}
                secondary={
                  <>
                    {currentUser ? currentUser.email : null}
                    {currentTenant && (
                      <Typography
                        component="span"
                        variant="caption"
                        display="block"
                        sx={{mt: 0.25, opacity: 0.7}}
                      >
                        {currentTenant.name}
                      </Typography>
                    )}
                  </>
                }
              />
            </MenuItem>
            {!isMobile && (
              <>
                <Divider component="li" sx={{my: 1}} />
                <NavLink to="/settings?sub_menu=personal&tab=user_profile">
                  <MenuItem id="user-profile-link">
                    <ListItemIcon>
                      <SettingsIcon />
                    </ListItemIcon>
                    <ListItemText sx={{color: 'text.secondary'}}>{t2('title')}</ListItemText>
                  </MenuItem>
                </NavLink>
              </>
            )}
            <Divider component="li" sx={{my: 1}} />
            {currentUser && (
              <Box sx={{px: 2, py: 0.5}}>
                <Picker
                  {...organizations}
                  options={withUnlistedSelection(
                    organizations.options,
                    currentTenantId ? [currentTenantId] : [],
                    organizationNames,
                  )}
                  label={t('profile.organization')}
                  value={currentTenantId ?? ''}
                  onChange={handleTenantChange}
                  placeholder={t('profile.organization')}
                  clearable={false}
                  size="small"
                  minWidth="0"
                  maxWidth="100%"
                  dataCy="appbar-organization"
                />
              </Box>
            )}

            <Divider component="li" sx={{my: 1}} />
            <MenuItem id="user-logout-button" onClick={logoutUser}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText sx={{color: 'text.secondary'}}>{t('profile.logout')}</ListItemText>
            </MenuItem>
          </MenuList>
        </Paper>
      </Popover>
    </StrictMode>
  )
}

export default AppbarProfile
